import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTokenStatus, clearAllAuthStorage } from 'utils/tokenManager';
import { API_BASE_URL } from 'config/apiConfig';

// CSS imports
import 'styles/Font.css';

// Components imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import Footer from "components/layout/Footer/Footer";

function SettingsPage() {
    usePreventHorizontalScroll();

    const navigate = useNavigate();

    // [핵심 1] 동적 높이 측정 (ResizeObserver 사용)
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(80);

    // --- State ---
    const [pushEnabled, setPushEnabled] = useState(true);
    const [marketingEnabled, setMarketingEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [modalInfo, setModalInfo] = useState(null);

    // 버전 정보 관련 상태 및 Ref
    const [tapCount, setTapCount] = useState(0);
    const [showWebVersion, setShowWebVersion] = useState(false);
    const [webVersion, setWebVersion] = useState('');
    const [appVersion, setAppVersion] = useState('1.2.0');
    const tapTimeoutRef = useRef(null);

    // [핵심 2] 헤더 크기 변화를 실시간으로 감지하는 ResizeObserver 적용
    useLayoutEffect(() => {
        const headerElement = headerRef.current;
        if (!headerElement) return;

        // 크기 변화 감지 로직
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // borderBoxSize가 지원되면 사용, 아니면 contentRect 사용
                if (entry.borderBoxSize) {
                    const borderBoxSize = entry.borderBoxSize[0] || entry.borderBoxSize;
                    setHeaderHeight(borderBoxSize.blockSize);
                } else {
                    setHeaderHeight(entry.contentRect.height);
                }
            }
        });

        // 관찰 시작
        observer.observe(headerElement);

        // 초기 실행 (혹시 모를 누락 방지)
        setHeaderHeight(headerElement.offsetHeight);

        // 클린업
        return () => {
            observer.disconnect();
        };
    }, []);

    // --- API: 설정 정보 불러오기 ---
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const { token } = getTokenStatus();
                if (!token) return;

                const response = await fetch(`${API_BASE_URL}/userAgree`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (response.ok) {
                    const data = await response.json();
                    const userInfo = data.userInfo || data.data?.userInfo || {};

                    if (userInfo) {
                        setPushEnabled(userInfo.agreementPush === 'Y');
                        setMarketingEnabled(userInfo.agreementMarketing === 'Y');
                    }
                }
            } catch (error) {
                console.error("Settings Load Error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, []);

    // 웹뷰 버전 정보 로드
    useEffect(() => {
        const localVersion = localStorage.getItem('appVersion') || '1.1.0';
        setWebVersion(localVersion);

        // 네이티브 앱 버전을 전달받는 전역 변수나 브릿지가 있는지 체크하여 바인딩
        if (window.appVersion) {
            setAppVersion(window.appVersion);
        }
    }, []);

    // 언마운트 시 타이머 클린업
    useEffect(() => {
        return () => {
            if (tapTimeoutRef.current) {
                clearTimeout(tapTimeoutRef.current);
            }
        };
    }, []);

    // 버전 정보 연속 터치 이벤트 핸들러
    const handleVersionClick = () => {
        if (showWebVersion) return;

        setTapCount((prev) => {
            const nextCount = prev + 1;
            if (nextCount >= 5) {
                setShowWebVersion(true);
                alert("개발자 모드 활성화: 웹뷰 버전 정보가 노출됩니다.");
                return 0;
            }
            return nextCount;
        });

        if (tapTimeoutRef.current) {
            clearTimeout(tapTimeoutRef.current);
        }
        tapTimeoutRef.current = setTimeout(() => {
            setTapCount(0);
        }, 1000);
    };

    // --- Handlers ---
    const handleToggle = async (type) => {
        const { token } = getTokenStatus();
        if (!token) {
            setModalInfo({ type: 'alert', title: '알림', message: '로그인이 필요합니다.', showCancel: false });
            return;
        }

        const nextPush = type === 'push' ? !pushEnabled : pushEnabled;
        const nextMarketing = type === 'marketing' ? !marketingEnabled : marketingEnabled;

        if (type === 'push') setPushEnabled(nextPush);
        if (type === 'marketing') setMarketingEnabled(nextMarketing);

        try {
            const payload = {
                agreementPush: nextPush ? 'Y' : 'N',
                agreementMarketing: nextMarketing ? 'Y' : 'N'
            };

            const response = await fetch(`${API_BASE_URL}/regAgree`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const data = await response.json();
            const regData = data.data || data;

            if (regData.code !== 200) {
                if (type === 'push') setPushEnabled(!nextPush);
                if (type === 'marketing') setMarketingEnabled(!nextMarketing);
                setModalInfo({ type: 'alert', title: '오류', message: '설정 변경에 실패했습니다.', showCancel: false });
            } else {
                const storedInfo = sessionStorage.getItem('userInfo');
                if (storedInfo) {
                    const parsedInfo = JSON.parse(storedInfo);
                    const updatedInfo = { ...parsedInfo, ...payload };
                    sessionStorage.setItem('userInfo', JSON.stringify(updatedInfo));
                }
            }

        } catch (error) {
            if (type === 'push') setPushEnabled(!nextPush);
            if (type === 'marketing') setMarketingEnabled(!nextMarketing);
            setModalInfo({ type: 'alert', title: '오류', message: '서버 통신 중 오류가 발생했습니다.', showCancel: false });
        }
    };

    const handlePolicyClick = (type) => {
        navigate(`/policy/app?type=${type}`);
    };

    const closeModal = () => setModalInfo(null);

    const handleConfirmAction = async () => {
        const type = modalInfo?.type;

        if (type === 'logout') {
            clearAllAuthStorage();
            setModalInfo(null);
            navigate('/', { replace: true });
        } else if (type === 'withdraw') {
            setModalInfo({
                type: 'success_withdraw',
                title: '알림',
                message: '탈퇴가 완료되었습니다.',
                showCancel: false
            });
        } else if (type === 'success_withdraw') {
            clearAllAuthStorage();
            setModalInfo(null);
            navigate('/', { replace: true });
        } else if (type === 'alert') {
            setModalInfo(null);
        }
    };

    if (isLoading) return <div style={styles.loading}>로딩 중...</div>;

    const dynamicMainContentStyle = {
        ...styles.mainContent,
        paddingTop: `${headerHeight + 10}px`,
        paddingBottom: '40px',
        paddingLeft: '24px',
        paddingRight: '24px',
    };

    return (
        <div style={styles.backgroundWrapper}>
            {/* 고정 헤더 */}
            <div style={styles.fixedHeaderWrapper} ref={headerRef}>
                <TopNavigator title="설정" />
            </div>

            {/* 메인 컨텐츠 */}
            <main style={dynamicMainContentStyle}>
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>알림 설정</h3>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>앱 푸시 알림</span>
                        <ToggleSwitch checked={pushEnabled} onChange={() => handleToggle('push')} />
                    </div>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>마케팅 정보 수신 동의</span>
                        <ToggleSwitch checked={marketingEnabled} onChange={() => handleToggle('marketing')} />
                    </div>
                </div>

                <div style={styles.divider} />

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>약관 및 정보</h3>
                    <div style={styles.linkRow} onClick={() => handlePolicyClick('terms')}>
                        <span style={styles.rowLabel}>서비스 이용약관</span>
                        <img src={require('assets/images/icon/arrow-right-icon.svg').default} alt="이동"
                            style={styles.arrowIcon} />
                    </div>
                    <div style={styles.linkRow} onClick={() => handlePolicyClick('privacy')}>
                        <span style={styles.rowLabel}>개인정보 처리방침</span>
                        <img src={require('assets/images/icon/arrow-right-icon.svg').default} alt="이동"
                            style={styles.arrowIcon} />
                    </div>
                    <div
                        style={{ ...styles.row, cursor: showWebVersion ? 'default' : 'pointer' }}
                        onClick={handleVersionClick}
                    >
                        <span style={styles.rowLabel}>버전 정보</span>
                        <span style={styles.versionValue}>
                            {appVersion}
                            {showWebVersion && ` (w${webVersion})`}
                        </span>
                    </div>
                </div>

                <Footer />
            </main>

            <ConfirmModal
                isOpen={!!modalInfo}
                onClose={closeModal}
                onConfirm={handleConfirmAction}
                title={modalInfo?.title}
                message={modalInfo?.message}
                showCancel={modalInfo?.showCancel}
            />
        </div>
    );
}

const ToggleSwitch = ({ checked, onChange }) => (
    <div
        onClick={onChange}
        style={{
            width: '44px', height: '24px', borderRadius: '12px',
            backgroundColor: checked ? '#8052FF' : '#3A394A',
            position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s', flexShrink: 0,
        }}
    >
        <div style={{
            width: '20px', height: '20px', backgroundColor: '#FFFFFF', borderRadius: '50%',
            position: 'absolute', top: '2px', left: '2px',
            transform: checked ? 'translateX(20px)' : 'translateX(0px)',
            transition: 'transform 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }} />
    </div>
);

const styles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 100, backgroundColor: '#0D042D',
    },
    mainContent: {
        flex: 1, overflowY: 'auto',
        paddingTop: 0,
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '40px',
        display: 'flex', flexDirection: 'column',
    },
    loading: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', background: '#0D042D', color: 'white'
    },
    section: {
        display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '24px',
    },
    sectionTitle: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#9A9A9A', marginBottom: '8px',
    },
    row: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '40px',
    },
    linkRow: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '40px', cursor: 'pointer',
    },
    rowLabel: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '16px', color: '#FFFFFF',
    },
    arrowIcon: {
        width: '7px', height: '14px', opacity: 0.7,
    },
    versionValue: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '16px', color: '#9A9A9A',
    },
    divider: {
        width: '100%', height: '1px', backgroundColor: '#3A394A', margin: '12px 0 32px 0',
    }
};

export default SettingsPage;